import React, { useEffect, useState } from "react";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import { FaEdit, FaPlus, FaTimes } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import {
  AddIndustry,
  DeleteIndustry,
  EditIndustry,
  FetchIndustries,
  UpdateIndustry,
} from "../../Redux/Slices/Industry";
import toast from "react-hot-toast";
import "../../asset/new/css/admin-modern.css";

const Industry = () => {
  const [industryName, setIndustryName] = useState("");
  const [editData, setEditData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { industriesData } = useSelector((state) => state?.Industry);

  //Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!industryName.trim()) {
      toast.error("Please enter industry name");
      return;
    }
    let inputData = { name: industryName };
    await AddIndustry(inputData);
    let data = {
      page:1,
      search:searchQuery
    };
    dispatch(FetchIndustries(data));
    setIndustryName("");
    setAddModalOpen(false);
    toast.success("Industry added successfully");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    let data = {
      page:1,
      search:e.target.value
    };
    // searchSalesHistory(data)
    dispatch(FetchIndustries(data));
    
  };

  // Edit
  const handleEdit = async (id) => {
    const data = await EditIndustry(id);
    setEditData(data?.data[0]);
    setEditModalOpen(true);
  };

  //Update
  const handleUpdate = async (id) => {
    if (!editData?.name?.trim()) {
      toast.error("Please enter industry name");
      return;
    }
    let inputData = { name: editData?.name };
    await UpdateIndustry(inputData, id);
    let data = {
      page:1,
      search:searchQuery
    };
    dispatch(FetchIndustries(data));
    setEditModalOpen(false);
    toast.success("Industry updated successfully");
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this industry?")) return;
    await DeleteIndustry(id);
    let data = {
      page:1,
      search:searchQuery
    };
    dispatch(FetchIndustries(data));
    toast.success("Industry deleted successfully");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let data = {
        page:1,
        search:searchQuery
      };
      await dispatch(FetchIndustries(data));
      setLoading(false);
    };
    fetchData();
    document.title = "Industry | Admin";
  }, [dispatch]);

  // Format the date
  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const formattedDate = dateObject
      .toISOString()
      .replace("T", " ")
      .replace("Z", "");
    return formattedDate.slice(0, -5);
  };

  const handlePageChange = async (page) => {
    let data = {
      page: page,
      search:searchQuery
    };
    dispatch(FetchIndustries(data));
  };

  // Calculate visible page range
  let startPage = 1;
  let endPage = 1;

  if (industriesData?.pagination) {
    startPage = Math.max(1, industriesData.pagination.page - 2);
    endPage = Math.min(
      industriesData.pagination.totalPages,
      industriesData.pagination.page + 2
    );

    // Adjust start and end page if close to beginning or end
    if (industriesData.pagination.page <= 3) {
      endPage = Math.min(5, industriesData.pagination.totalPages);
    } else if (
      industriesData.pagination.page >=
      industriesData.pagination.totalPages - 2
    ) {
      startPage = Math.max(1, industriesData.pagination.totalPages - 4);
    }
  }
  return (
    <AdminLayout
      title="Industries"
      subtitle="Manage industry categories"
      actions={
        <button
          onClick={() => setAddModalOpen(true)}
          className="admin-btn admin-btn-primary"
        >
          <FaPlus className="me-2" />
          Add Industry
        </button>
      }
    >
      {/* Search Bar */}
      <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="admin-card-body">
          <input
            type="search"
            placeholder="Search industries..."
            className="admin-input"
            value={searchQuery}
            onChange={(e) => handleSearch(e)}
            style={{ maxWidth: "500px" }}
          />
        </div>
      </div>

      {/* Table Card */}
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Industry Name</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSkeleton type="table-row" count={5} columns={4} />
                ) : industriesData?.docs?.length > 0 ? (
                  industriesData.docs.map((item, index) => (
                    <tr key={item._id}>
                      <td>
                        <span className="admin-badge admin-badge-light">
                          {(industriesData.pagination?.page - 1) * (industriesData.pagination?.limit || 10) + index + 1}
                        </span>
                      </td>
                      <td>{item?.name}</td>
                      <td>{formatDate(item?.created_at)}</td>
                      <td>
                        <div className="admin-btn-group">
                          <button
                            onClick={() => handleEdit(item?._id)}
                            className="admin-btn admin-btn-sm admin-btn-warning"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(item?._id)}
                            className="admin-btn admin-btn-sm admin-btn-danger"
                            title="Delete"
                          >
                            <AiFillDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="admin-empty-state">
                        <i className="fas fa-industry fa-3x mb-3"></i>
                        <h4>No Industries Found</h4>
                        <p>Start by adding your first industry category.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {industriesData?.pagination && industriesData?.docs?.length > 0 && (
            <div className="admin-pagination">
              <button
                className="admin-pagination-btn"
                onClick={() => handlePageChange(industriesData.pagination.prevPage)}
                disabled={!industriesData.pagination.prevPage}
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>

              <div className="admin-pagination-pages">
                {Array.from(
                  { length: endPage - startPage + 1 },
                  (_, i) => startPage + i
                ).map((page) => (
                  <button
                    key={page}
                    className={`admin-pagination-page ${
                      industriesData.pagination.page === page ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="admin-pagination-btn"
                onClick={() => handlePageChange(industriesData.pagination.nextPage)}
                disabled={!industriesData.pagination.nextPage}
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {addModalOpen && (
        <div className="admin-modal-backdrop" onClick={() => setAddModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Add Industry</h3>
              <button onClick={() => setAddModalOpen(false)} className="admin-modal-close">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label className="admin-form-label">Industry Name</label>
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Enter industry name"
                    value={industryName}
                    onChange={(e) => setIndustryName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="admin-btn admin-btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  Add Industry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editData && (
        <div className="admin-modal-backdrop" onClick={() => setEditModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Industry</h3>
              <button onClick={() => setEditModalOpen(false)} className="admin-modal-close">
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Industry Name</label>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Enter industry name"
                  value={editData?.name || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  autoFocus
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={() => setEditModalOpen(false)}
                className="admin-btn admin-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdate(editData._id)}
                className="admin-btn admin-btn-primary"
              >
                Update Industry
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Industry;
